import React, {useState} from 'react';
import {View, PixelRatio, TouchableOpacity, NativeSyntheticEvent, LayoutChangeEvent} from 'react-native';
import {Text} from '../../../../shared/components/text';
import {useStyles} from '../../../../shared/hooks/styles';
import {DeviceSize} from '../../../../shared/theme/helpers';
import {LoadableContent} from '../../../../shared/components/loadableContent';
import {useSelector} from 'react-redux';
import {dobroruDataLoadingSelector, dobroruDataSelector} from '../../../../redux/selectors/dobroru';
import {images} from '../../../../assets/icons';
import {IconLink} from '../../../../shared/components/menu/components/iconLink';
import Tooltip from 'react-native-walkthrough-tooltip';
import {useLogoutDobroRuDialog} from '../../../../shared/hooks/useLogoutDobroRuDialog';

export const Info = () => {
  const styles = useStyles(({theme, isDesktop, mediaOnly}) => ({
    root: {
      marginBottom: 30,
      backgroundColor: theme.color.backgroundMenuColor,
      ...mediaOnly(
        DeviceSize.desktop,
        {
          padding: 24,
          borderRadius: 5,
        },
        {
          paddingHorizontal: 16,
          paddingVertical: 20,
          borderRadius: 24,
        },
      ),
    },
    loader: {
      marginVertical: 0,
      width: '100%',
    },
    column: mediaOnly(DeviceSize.desktop, {}, {flexDirection: 'row', alignItems: 'center'}),
    idColumn: mediaOnly(DeviceSize.desktop, {minWidth: 170, marginRight: 30}, {marginBottom: 20}),
    name: {
      color: theme.color.grayMenu,
      ...mediaOnly(DeviceSize.desktop, theme.fonts.body, {...theme.fonts.bodySmallLight, width: 130}),
    },
    id: mediaOnly(
      DeviceSize.desktop,
      {
        marginTop: 14,
        ...theme.fonts.h4,
      },
      theme.fonts.action,
    ),
    hours: mediaOnly(
      DeviceSize.desktop,
      {
        marginTop: 4,
        ...theme.fonts.h2,
      },
      theme.fonts.h5,
    ),

    list: {
      flexDirection: isDesktop ? 'row' : 'column',
      flexWrap: 'wrap',
      gap: '1em',
    },
    listItem: {
      flexDirection: isDesktop ? 'column' : 'row',
      justifyContent: 'space-between',
      ...mediaOnly(
        DeviceSize.desktop,
        {
          flexGrow: 1,
          flexShrink: 1,
          flexBasis: '20%',
        },
        {
          marginBottom: 20,
        },
      ),
    },
    listTitleText: {
      ...(isDesktop ? theme.fonts.body : theme.fonts.bodySmallLight),
      whiteSpace: 'break-spaces',
      color: theme.color.labelText,
    },
    listItemTitle: {
      ...mediaOnly(
        DeviceSize.desktop,
        {},
        {
          flexBasis: 'auto',
          minWidth: 94 * PixelRatio.getFontScale(),
          marginRight: 20,
        },
      ),
    },
    listItemValue: {
      ...mediaOnly(
        DeviceSize.desktop,
        {},
        {
          flex: 1,
          alignItems: 'flex-end',
        },
      ),
    },
    listItemLastValue: {
      ...(isDesktop
        ? {
            ...theme.fonts.h1,
            marginBottom: 5,
          }
        : {
            ...theme.fonts.action,
            marginBottom: 1,
          }),
    },
    listValueText: {
      ...(isDesktop
        ? {
            ...theme.fonts.h6Light,
            marginTop: 5,
          }
        : theme.fonts.body),
      color: theme.color.textColor,
    },
    listValueTextBold: {
      ...(isDesktop
        ? {
            ...theme.fonts.h5,
            marginBottom: 10,
          }
        : {
            ...theme.fonts.action,
            marginBottom: 1,
          }),
      color: theme.color.primaryColor,
    },
    listValueTextSmall: {
      ...(isDesktop ? theme.fonts.body : theme.fonts.bodySmallLight),
      color: theme.color.labelText,
    },
    flexRow: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'baseline',
    },
    singOutIcon: {
      paddingLeft: 10,
    },
    tooltipArrowStyle: {left: 0},
    tooltipContainerStyle: {
      left: 200,
      boxShadow: 'unset',
    },
    tooltipModalStyle: {
      display: 'flex',
      flexDirection: 'row',
      backgroundColor: theme.color.cardText,
      height: 'auto',
      width: 'auto',
    },
    tooltipOverlayStyle: {opacity: 0, display: 'flex', alignItems: 'flex-start'},
  }));

  const data = useSelector(dobroruDataSelector);
  const loading = useSelector(dobroruDataLoadingSelector);
  const logoutDobroru = useLogoutDobroRuDialog();
  const [show, setShow] = useState(false);

  const signOutDobroRu = () => {
    logoutDobroru();
  };

  return (
    <LoadableContent style={styles.root} loading={loading}>
      <View style={styles.list}>
        <View style={styles.listItem}>
          <View style={[styles.listItemTitle, styles.flexRow]}>
            <Text testID='card-balance-id' style={styles.listTitleText} numberOfLines={1}>
              Волонтер ID
            </Text>
            <View style={styles.singOutIcon}>
              <TouchableOpacity testID='signout-dobroru-id' activeOpacity={1}>
                <Tooltip
                  placement='bottom'
                  onClose={() => setShow(false)}
                  arrowStyle={styles.tooltipArrowStyle}
                  tooltipStyle={styles.tooltipContainerStyle}
                  childrenWrapperStyle={styles.tooltipOverlayStyle}
                  contentStyle={styles.tooltipModalStyle}
                  backgroundColor='white'
                  isVisible={show}
                  content={
                    <IconLink
                      testID='signout-dobroru-test-id'
                      icon={images.SignOut}
                      iconHeight={16}
                      iconWidth={16}
                      onPress={signOutDobroRu}
                    />
                  }>
                  <Text numberOfLines={1} ellipsizeMode='tail' title='Выход из учетной записи Добро.ру'>
                    <IconLink
                      testID='signout-dobroru-test-id'
                      icon={images.SignOut}
                      iconHeight={16}
                      iconWidth={16}
                      onPress={signOutDobroRu}
                    />
                  </Text>
                </Tooltip>
              </TouchableOpacity>
            </View>
          </View>
          <View style={styles.listItemValue}>
            <Text testID='card-balance-value-id' style={styles.listValueTextBold}>
              {data?.volunteerId}
            </Text>
          </View>
        </View>
        <View style={styles.listItem}>
          <View style={styles.listItemTitle}>
            <Text testID='card-balance-id' style={styles.listTitleText} numberOfLines={1}>
              Часов получено
            </Text>
          </View>
          <View style={styles.listItemValue}>
            <Text testID='card-balance-value-id' style={styles.listValueTextBold}>
              {data?.hours}
            </Text>
          </View>
        </View>
        <View style={styles.listItem}>
          <View style={styles.listItemTitle}>
            <Text testID='card-balance-id' style={styles.listTitleText} numberOfLines={1}>
              Баллов использовано
            </Text>
          </View>
          <View style={styles.listItemValue}>
            <Text testID='card-balance-value-id' style={styles.listValueTextBold}>
              {data?.spentScores}
            </Text>
          </View>
        </View>
        <View style={styles.listItem}>
          <View style={styles.listItemTitle}>
            <Text testID='card-balance-id' style={styles.listTitleText} numberOfLines={1}>
              Доступно баллов
            </Text>
          </View>
          <View style={styles.listItemValue}>
            <Text testID='card-balance-value-id' style={[styles.listValueTextBold, styles.listItemLastValue]}>
              {data?.actualScores}
            </Text>
          </View>
        </View>
      </View>
    </LoadableContent>
  );
};
